/**
 * VoiceRecorder - Manages audio recording via MediaRecorder API
 * Supports Flutter WebView microphone permission requests
 *
 * The OS can silence the microphone mid-recording (app sent to background, screen
 * locked, incoming call, Bluetooth headset switching). MediaRecorder keeps running and
 * writes silence, so the recipient got voice messages that went mute halfway through.
 * The recorder now pauses while the microphone is unavailable and reports it through
 * `onInterrupted` / `onResumed`, so the sender knows and the silence is not recorded.
 *
 * The screen is also kept on while recording, so the automatic screen lock — the one
 * interruption the app can prevent — no longer cuts the microphone off.
 */

// Preferred containers, best first. Safari only supports audio/mp4; Chrome and Firefox
// support webm/ogg. Whatever is picked, the file is labelled with the recorder's real
// MIME type — iOS recordings used to be sent as "audio/webm" while holding MP4 bytes.
const PREFERRED_MIME_TYPES = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",
    "audio/ogg;codecs=opus"
]

const EXTENSIONS = {
    "audio/webm": "webm",
    "audio/mp4": "m4a",
    "audio/ogg": "ogg",
    "audio/mpeg": "mp3",
    "audio/aac": "aac"
}

export default class VoiceRecorder {
    constructor() {
        this.mediaRecorder = null
        this.audioChunks = []
        this.isRecording = false
        this.isInterrupted = false
        this.elapsedBeforePause = 0
        this.startTime = null
        this.timerInterval = null
        this.onTimerUpdate = null
        this.onInterrupted = null
        this.onResumed = null
        this.wakeLock = null
        this._handleVisibilityChange = this._handleVisibilityChange.bind(this)
    }

    get isInFlutterApp() {
        return typeof window.flutter_inappwebview !== "undefined"
    }

    /**
     * @param {Object} callbacks
     * @param {Function} [callbacks.onTimerUpdate] receives the "mm:ss" recorded time
     * @param {Function} [callbacks.onInterrupted] receives { resumable } when the microphone
     *   is cut off; resumable is false once the track has ended for good
     * @param {Function} [callbacks.onResumed] called when the microphone is back
     */
    async start({onTimerUpdate, onInterrupted, onResumed} = {}) {
        this.onTimerUpdate = onTimerUpdate
        this.onInterrupted = onInterrupted
        this.onResumed = onResumed

        if (this.isInFlutterApp) {
            const hasPermission = await this._requestFlutterMicPermission()
            if (!hasPermission) return false
        }

        try {
            const stream = await navigator.mediaDevices.getUserMedia({audio: true})
            const mimeType = this._supportedMimeType()
            this.mediaRecorder = new MediaRecorder(stream, mimeType ? {mimeType} : undefined)
            this.audioChunks = []
            this.isInterrupted = false
            this.elapsedBeforePause = 0

            this.mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) this.audioChunks.push(event.data)
            }

            this._recordingPromise = new Promise((resolve) => {
                this.mediaRecorder.onstop = () => {
                    const blob = new Blob(this.audioChunks, {type: this._recordedMimeType()})
                    stream.getTracks().forEach(track => track.stop())
                    resolve(blob)
                }
            })

            this._watchTrack(stream.getAudioTracks()[0])
            document.addEventListener("visibilitychange", this._handleVisibilityChange)

            this.mediaRecorder.start()
            this.isRecording = true
            this.startTime = Date.now()
            this._startTimer()
            this._keepScreenOn(true)

            return true
        } catch (error) {
            console.error("Error accessing microphone:", error)
            return false
        }
    }

    async stop() {
        if (!this.mediaRecorder || !this.isRecording) return null

        this._teardown()
        this.mediaRecorder.stop()

        const blob = await this._recordingPromise
        const type = blob.type || "audio/webm"
        return new File([blob], `recording_${Date.now()}.${this._extensionFor(type)}`, {type})
    }

    cancel() {
        if (!this.mediaRecorder || !this.isRecording) return

        this._teardown()
        this.mediaRecorder.ondataavailable = null
        this.mediaRecorder.onstop = () => {
            this.mediaRecorder.stream?.getTracks().forEach(track => track.stop())
        }
        this.mediaRecorder.stop()

        this.audioChunks = []
    }

    // ===== Interruptions =====

    _watchTrack(track) {
        if (!track) return
        this.track = track

        // `mute`: the OS took the microphone away temporarily (call, other app, background).
        // `ended`: it is gone for good (device unplugged, Bluetooth switch); it cannot resume.
        track.onmute = () => this._interrupt()
        track.onunmute = () => this._resume()
        track.onended = () => this._interrupt({resumable: false})
    }

    _handleVisibilityChange() {
        if (document.visibilityState === "hidden") {
            this._interrupt()
        } else {
            // The browser drops the screen wake lock whenever the page is hidden.
            if (this.isRecording) this._acquireWakeLock()
            if (this.track && !this.track.muted && this.track.readyState === "live") this._resume()
        }
    }

    _interrupt({resumable = true} = {}) {
        if (!this.isRecording) return

        if (!this.isInterrupted) {
            this.isInterrupted = true
            if (this.mediaRecorder.state === "recording") this.mediaRecorder.pause()
            this._pauseTimer()
        }

        if (this.onInterrupted) this.onInterrupted({resumable})
    }

    _resume() {
        if (!this.isRecording || !this.isInterrupted) return
        if (this.track?.readyState !== "live") return

        this.isInterrupted = false
        if (this.mediaRecorder.state === "paused") this.mediaRecorder.resume()
        this.startTime = Date.now()
        this._startTimer()

        if (this.onResumed) this.onResumed()
    }

    _teardown() {
        this._keepScreenOn(false)
        this.isRecording = false
        this.isInterrupted = false
        document.removeEventListener("visibilitychange", this._handleVisibilityChange)
        if (this.track) {
            this.track.onmute = null
            this.track.onunmute = null
            this.track.onended = null
            this.track = null
        }
        this._stopTimer()
    }

    // ===== Screen wake lock =====

    // In the Flutter app the WebView's Wake Lock API is unreliable (missing on Android), so
    // the native side holds the lock through the `setKeepScreenOn` handler. Neither call is
    // awaited: an app version without the handler must not block or break the recording.
    _keepScreenOn(enabled) {
        if (this.isInFlutterApp) {
            Promise.resolve()
                .then(() => window.flutter_inappwebview.callHandler("setKeepScreenOn", enabled))
                .catch(() => {})
        }

        if (enabled) {
            this._acquireWakeLock()
        } else {
            this._releaseWakeLock()
        }
    }

    async _acquireWakeLock() {
        if (!("wakeLock" in navigator) || this.wakeLock || document.visibilityState !== "visible") return

        try {
            const wakeLock = await navigator.wakeLock.request("screen")
            // The recording may have stopped while the request was pending.
            if (!this.isRecording) return wakeLock.release()

            this.wakeLock = wakeLock
            wakeLock.addEventListener("release", () => {
                if (this.wakeLock === wakeLock) this.wakeLock = null
            })
        } catch (_error) {
            // Refused (battery saver, permissions policy…): recording works without it.
        }
    }

    _releaseWakeLock() {
        const wakeLock = this.wakeLock
        this.wakeLock = null
        wakeLock?.release().catch(() => {})
    }

    // ===== MIME type =====

    _supportedMimeType() {
        if (typeof MediaRecorder.isTypeSupported !== "function") return null
        return PREFERRED_MIME_TYPES.find(type => MediaRecorder.isTypeSupported(type)) || null
    }

    // The type the recorder actually produced, without codec parameters
    // ("audio/webm;codecs=opus" -> "audio/webm") so it matches server-side content types.
    _recordedMimeType() {
        const type = this.mediaRecorder?.mimeType || this.audioChunks[0]?.type || "audio/webm"
        return type.split(";")[0].trim()
    }

    _extensionFor(type) {
        return EXTENSIONS[type] || "webm"
    }

    // ===== Timer (counts recorded time only, interruptions excluded) =====

    _startTimer() {
        this._clearTimerInterval()
        this.timerInterval = setInterval(() => this._emitTime(), 1000)
    }

    _pauseTimer() {
        this.elapsedBeforePause += Date.now() - this.startTime
        this._clearTimerInterval()
        this._emitTime()
    }

    _emitTime() {
        const running = this.isInterrupted ? 0 : Date.now() - this.startTime
        const elapsed = Math.floor((this.elapsedBeforePause + running) / 1000)
        const minutes = Math.floor(elapsed / 60)
        const seconds = elapsed % 60
        const formatted = `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`

        if (this.onTimerUpdate) this.onTimerUpdate(formatted)
    }

    _clearTimerInterval() {
        if (this.timerInterval) {
            clearInterval(this.timerInterval)
            this.timerInterval = null
        }
    }

    _stopTimer() {
        this._clearTimerInterval()
        this.elapsedBeforePause = 0
        if (this.onTimerUpdate) this.onTimerUpdate("00:00")
    }

    async _requestFlutterMicPermission() {
        try {
            const result = await window.flutter_inappwebview.callHandler("requestMicrophonePermission")
            return result?.granted || result === true
        } catch (error) {
            console.error("Error requesting microphone permission:", error)
            return true
        }
    }
}
